import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase/config";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { X, Search, MessageCircleQuestion } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import useConfigSitio from "../hooks/useConfigSitio";

const TEXT_DEFAULT = encodeURIComponent(
  "Hola 👋, tengo una consulta que no aparece en las preguntas frecuentes."
);

function Accordion({ question, answer, isOpen, onToggle }) {
  return (
    <div className="border rounded-lg">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left px-3 py-2"
      >
        <span className="font-medium text-gray-900">{question}</span>
      </button>
      {isOpen && (
        <div className="px-3 pb-3 text-sm text-gray-700 whitespace-pre-wrap">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function FloatingFAQ() {
  const navigate = useNavigate();
  const { config, loading } = useConfigSitio();

  const [open, setOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const [catId, setCatId] = useState("");
  const [items, setItems] = useState([]);
  const [openIdx, setOpenIdx] = useState(null);
  const [search, setSearch] = useState("");

  let waNumber = "56976604702";
  let waUrl = `https://wa.me/${waNumber}?text=${TEXT_DEFAULT}`;

  if (!loading && config?.whatsapp) {
    const digits = config.whatsapp.replace(/\D/g, "");
    if (digits.length >= 8) {
      waNumber = digits;
      waUrl = `https://wa.me/${waNumber}?text=${TEXT_DEFAULT}`;
    }
  }

  useEffect(() => {
    const qCats = query(collection(db, "faq_categories"), orderBy("order", "asc"));
    const unsub = onSnapshot(qCats, (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      setCats(arr);
      if (!catId && arr.length) setCatId(arr[0].id);
    });
    return () => unsub();
  }, [catId]);

  useEffect(() => {
    if (!catId) return;
    const qItems = query(
      collection(db, "faq_categories", catId, "items"),
      orderBy("order", "asc")
    );
    const unsub = onSnapshot(qItems, (snap) => {
      const arr = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((i) => i.active !== false);
      setItems(arr);
      setOpenIdx(null);
    });
    return () => unsub();
  }, [catId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        it.q?.toLowerCase().includes(q) || it.a?.toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <>
      {!open && (
        <div className="fixed bottom-4 right-4 md:bottom-5 md:right-5 z-40 flex flex-col items-center gap-3 animate-fade-in-up">
          {/* FAQ */}
          <button
            aria-label="Abrir preguntas frecuentes"
            onClick={() => setOpen(true)}
            className="
              group
              h-12 w-12
              rounded-full
              bg-[#06b6fa] text-white
              flex items-center justify-center
              shadow-[0_10px_30px_rgba(0,0,0,0.22),0_3px_10px_rgba(0,0,0,0.12)]
              hover:-translate-y-1 hover:scale-105
              active:scale-95
              transition-all duration-300
              animate-float-soft
            "
            title="Preguntas frecuentes"
          >
            <MessageCircleQuestion
              size={21}
              className="transition-transform duration-300 group-hover:scale-110"
            />
          </button>

          {/* WhatsApp */}
          <a
            href={waUrl}
            target="_blank"
            rel="noreferrer"
            aria-label="Hablar por WhatsApp"
            title="Hablar por WhatsApp"
            className="
              group
              h-16 w-16
              rounded-full
              bg-[#25D366] text-white
              flex items-center justify-center
              shadow-[0_14px_34px_rgba(0,0,0,0.24),0_4px_14px_rgba(0,0,0,0.14)]
              hover:-translate-y-1 hover:scale-105 hover:bg-[#1EBE5D]
              active:scale-95
              transition-all duration-300
              animate-float-soft-delay
            "
          >
            <FaWhatsapp className="text-[32px] transition-transform duration-300 group-hover:scale-110" />
          </a>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
          />

          <div className="absolute right-0 top-0 h-full w-full sm:w-[480px] bg-white shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="font-semibold text-blue-900">Preguntas Frecuentes</div>
              <button
                className="p-2 rounded hover:bg-gray-100"
                onClick={() => setOpen(false)}
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4 space-y-3 border-b">
              <div className="flex gap-2 overflow-x-auto no-scrollbar">
                {cats.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => {
                      setCatId(c.id);
                      setSearch("");
                      setOpenIdx(null);
                    }}
                    className={`px-3 py-1.5 text-sm rounded-full border whitespace-nowrap ${
                      c.id === catId
                        ? "bg-blue-600 text-white border-blue-600"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {c.title}
                  </button>
                ))}
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2" size={16} />
                <input
                  className="w-full pl-9 pr-3 py-2 border rounded-lg"
                  placeholder="Buscar en esta categoría…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="p-4 overflow-y-auto grow">
              {filtered.length === 0 ? (
                <div className="text-gray-500 border rounded-lg p-3">
                  No encontramos resultados. Prueba otra palabra o contáctanos por WhatsApp.
                </div>
              ) : (
                <div className="space-y-2">
                  {filtered.map((it, idx) => (
                    <Accordion
                      key={it.id}
                      question={it.q}
                      answer={it.a}
                      isOpen={openIdx === idx}
                      onToggle={() => setOpenIdx((o) => (o === idx ? null : idx))}
                    />
                  ))}
                </div>
              )}

              <a
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
              >
                <FaWhatsapp className="text-[18px]" />
                Hablar por WhatsApp
              </a>

              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate("/faq");
                }}
                className="w-full text-center text-blue-600 text-sm mt-3 hover:underline"
              >
                Ver página de FAQ completa
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}